import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Map as MapIcon, FileText, MessagesSquare, Check, MapPin,
  Bell, Sun, Home, CalendarCheck, BookOpen, LifeBuoy, User, Phone, Mail,
  MessageCircle, MoreHorizontal, ChevronDown, ChevronRight,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const TRIP_IMG = "/hero-amalfi-1366.webp";
const LAZY = { loading: "lazy", decoding: "async" };

const MENU = [
  { icon: Home, label: "Minha viagem" },
  { icon: MapIcon, label: "Roteiro" },
  { icon: CalendarCheck, label: "Reservas" },
  { icon: FileText, label: "Documentos" },
  { icon: BookOpen, label: "Guias de destino" },
  { icon: LifeBuoy, label: "Suporte" },
  { icon: User, label: "Perfil" },
];

const ETAPAS = [
  { icon: Check, label: "Planejamento", status: "Concluído", state: "done" },
  { icon: Check, label: "Reservas", status: "Concluído", state: "done" },
  { icon: FileText, label: "Documentos", status: "Em andamento", state: "doing" },
  { icon: MapPin, label: "Destino", status: "Em breve", state: "todo" },
];

const QUICK = [
  { icon: MapIcon, label: "Roteiro completo" },
  { icon: CalendarCheck, label: "Minhas reservas" },
  { icon: FileText, label: "Documentos de viagem" },
  { icon: BookOpen, label: "Guias de destino" },
];

const FEATURE_ICONS = [MapIcon, FileText, MessagesSquare];

function CompassWatermark() {
  return (
    <svg
      aria-hidden
      className="absolute -top-10 -right-10 w-[380px] h-[380px] text-[#D9A854] opacity-[0.08] pointer-events-none"
      viewBox="0 0 200 200"
      fill="none"
      stroke="currentColor"
    >
      <circle cx="100" cy="100" r="96" strokeWidth="1.5" />
      <circle cx="100" cy="100" r="72" strokeWidth="1" />
      <circle cx="100" cy="100" r="40" strokeWidth="0.75" />
      <path d="M100 4 L112 100 L100 196 L88 100 Z" strokeWidth="1" fill="currentColor" fillOpacity="0.35" />
      <path d="M4 100 L100 88 L196 100 L100 112 Z" strokeWidth="1" fill="currentColor" fillOpacity="0.35" />
      <path d="M32 32 L100 92 L168 168 L100 108 Z" strokeWidth="0.75" fill="currentColor" fillOpacity="0.2" />
      <path d="M168 32 L108 100 L32 168 L92 100 Z" strokeWidth="0.75" fill="currentColor" fillOpacity="0.2" />
    </svg>
  );
}

export default function TechPlatform() {
  const { t } = useLang();
  const navigate = useNavigate();
  const features = t("tech.features");

  return (
    <section id="plataforma" data-testid="tech-platform-section" className="relative bg-[#0B2E4A] text-[#FAF6EE] py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img src={TRIP_IMG} alt="" className="w-full h-full object-cover opacity-25" {...LAZY} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B2E4A] via-[#0B2E4A]/85 to-[#0B2E4A]/95" />
      </div>
      <CompassWatermark />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D9A854] mb-4">{t("tech.eyebrow")}</p>
          <h2 className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] text-balance">
            {t("tech.title")} <span className="italic text-[#D9A854]">{t("tech.titleAccent")}</span>
          </h2>
          <p className="mt-5 text-[#F1E7D6]/70 text-base sm:text-lg leading-relaxed max-w-lg">{t("tech.text")}</p>
          <button
            data-testid="tech-platform-cta"
            onClick={() => navigate("/login")}
            className="mt-9 inline-flex items-center gap-2 bg-[#D9A854] hover:bg-[#C99944] text-[#0B2E4A] font-semibold px-8 py-4 rounded-full transition-all duration-200 shadow-xl shadow-[#D9A854]/25 hover:scale-[1.03] active:scale-95"
          >
            {t("tech.cta")} <ArrowRight size={17} />
          </button>

          <ul className="mt-10 space-y-4">
            {Array.isArray(features) && features.map((f, i) => {
              const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
              return (
                <li key={i} className="flex items-center gap-3.5">
                  <span className="w-9 h-9 rounded-full border border-[#D9A854]/30 bg-[#D9A854]/10 flex items-center justify-center shrink-0">
                    <Icon size={15} className="text-[#D9A854]" />
                  </span>
                  <span className="text-sm font-semibold text-[#F1E7D6]/85">{f}</span>
                </li>
              );
            })}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative pb-12"
        >
          {/* Laptop */}
          <div className="relative rounded-t-[1.4rem] bg-[#101E29] p-2 sm:p-2.5 pb-0 border border-white/10 shadow-[0_40px_90px_-24px_rgba(0,0,0,0.6)]">
            <div className="rounded-t-xl overflow-hidden bg-[#FAF6EE]">
              <div className="flex min-h-[320px] sm:min-h-[400px]">
                {/* Sidebar */}
                <div className="hidden sm:flex w-32 shrink-0 flex-col bg-[#0B2E4A] p-2.5">
                  <span className="flex items-center gap-1.5 px-1 pt-1 pb-3">
                    <img src="/brand/logo-emblem.png" alt="WYD" className="w-5 h-5" />
                    <span className="font-display font-semibold text-[#FAF6EE] text-[10px] leading-none">
                      WYD
                      <span className="block text-[4.5px] tracking-[0.2em] text-[#D9A854] uppercase font-sans mt-0.5">Personal travelling</span>
                    </span>
                  </span>
                  <div className="flex flex-col gap-1">
                    {MENU.map((m, i) => (
                      <span
                        key={m.label}
                        className={`flex items-center gap-2 rounded-lg px-2 py-2 text-[8.5px] font-semibold ${
                          i === 0 ? "bg-[#1F4F67] text-[#FAF6EE]" : "text-[#F1E7D6]/50"
                        }`}
                      >
                        <m.icon size={11} className={i === 0 ? "text-[#D9A854]" : ""} /> {m.label}
                      </span>
                    ))}
                  </div>
                  <div className="mt-auto relative rounded-lg overflow-hidden">
                    <img src={TRIP_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" {...LAZY} />
                    <div className="absolute inset-0 bg-[#0B2E4A]/55" />
                    <div className="relative p-2 flex items-end justify-between gap-1 min-h-[64px]">
                      <span className="font-display italic font-medium text-white text-[9px] leading-tight">
                        Qual é o<br />seu desejo?
                      </span>
                      <span className="w-4 h-4 shrink-0 rounded-full border border-[#D9A854] flex items-center justify-center">
                        <ChevronRight size={8} className="text-[#D9A854]" />
                      </span>
                    </div>
                  </div>
                </div>

                {/* Main */}
                <div className="flex-1 p-3 sm:p-4 min-w-0">
                  <div className="flex items-center justify-between mb-2.5">
                    <p className="font-display font-semibold text-[#0B2E4A] text-base sm:text-lg">Minha viagem</p>
                    <span className="flex items-center gap-2 text-[#56636D]">
                      <span className="relative">
                        <Bell size={11} />
                        <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#C8662E]" />
                      </span>
                      <span className="w-5 h-5 rounded-full bg-[#D9A854] text-[#0B2E4A] text-[7px] font-bold flex items-center justify-center">MC</span>
                      <span className="text-[8px] font-semibold hidden sm:flex items-center gap-0.5">Olá, Mariana <ChevronDown size={8} /></span>
                    </span>
                  </div>

                  <div className="relative rounded-lg overflow-hidden">
                    <img src={TRIP_IMG} alt="Costa Amalfitana" className="w-full aspect-[16/7] object-cover" {...LAZY} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B2E4A]/75 via-[#0B2E4A]/10 to-transparent" />
                    <span className="absolute bottom-2 right-2.5 flex items-center gap-1 text-white">
                      <Sun size={9} className="text-[#D9A854]" />
                      <span className="text-[8px] font-bold">24°C</span>
                      <span className="text-[7px] text-white/85">Positano, Itália</span>
                    </span>
                    <div className="absolute bottom-2 left-2.5">
                      <p className="text-[6.5px] font-bold uppercase tracking-[0.18em] text-[#D9A854]">Próxima viagem</p>
                      <p className="font-display font-semibold text-white text-base sm:text-xl leading-tight">Costa Amalfitana</p>
                      <p className="text-[8px] text-[#F1E7D6]/85 mb-1.5">12 – 20 de Maio, 2026</p>
                      <span className="inline-flex items-center gap-1 bg-[#D9A854] text-[#0B2E4A] text-[8px] font-bold px-2.5 py-1.5 rounded-full">
                        Ver roteiro completo <ArrowRight size={8} />
                      </span>
                    </div>
                  </div>

                  <p className="mt-3 mb-1.5 font-display font-semibold text-[#0B2E4A] text-[11px] sm:text-sm">Etapas da sua viagem</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {ETAPAS.map((e) => (
                      <div key={e.label} className="text-center py-1">
                        <span
                          className={`mx-auto w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center border ${
                            e.state === "done"
                              ? "bg-[#7A9B8D] border-[#7A9B8D] text-white"
                              : e.state === "doing"
                                ? "bg-[#D9A854]/10 border-[#D9A854] text-[#D9A854]"
                                : "bg-[#F1E7D6] border-[#E6DED1] text-[#56636D]"
                          }`}
                        >
                          <e.icon size={11} />
                        </span>
                        <p className="mt-1 text-[7.5px] sm:text-[8.5px] font-bold text-[#0B2E4A] leading-tight">{e.label}</p>
                        <p className={`text-[6.5px] sm:text-[7.5px] ${e.state === "doing" ? "text-[#7A571C] font-semibold" : "text-[#56636D]"}`}>{e.status}</p>
                      </div>
                    ))}
                  </div>

                  <p className="mt-3 mb-1.5 font-display font-semibold text-[#0B2E4A] text-[11px] sm:text-sm">Acesso rápido</p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {QUICK.map((q) => (
                      <div key={q.label} className="bg-white rounded-lg border border-[#E6DED1] p-2 text-center">
                        <q.icon size={13} className="mx-auto text-[#0B2E4A]" />
                        <p className="mt-1 text-[6.5px] sm:text-[7.5px] font-semibold text-[#56636D] leading-tight">{q.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 hidden sm:grid grid-cols-2 gap-2">
                    <div>
                      <p className="mb-1 font-display font-semibold text-[#0B2E4A] text-[11px]">Próximos passos</p>
                      <div className="flex items-center gap-2 bg-white rounded-lg border border-[#E6DED1] px-2.5 py-2">
                        <FileText size={11} className="text-[#7A571C] shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[8px] font-bold text-[#0B2E4A] leading-tight">Enviar dados dos passaportes</span>
                          <span className="block text-[6.5px] text-[#56636D] leading-tight">Precisamos dos dados para emitir alguns serviços.</span>
                        </span>
                        <span className="shrink-0 bg-[#D9A854] text-[#0B2E4A] text-[7px] font-bold px-2 py-1 rounded-full flex items-center gap-0.5">
                          Enviar agora <ArrowRight size={7} />
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="mb-1 font-display font-semibold text-[#0B2E4A] text-[11px]">Fale com seu consultor</p>
                      <div className="flex items-center gap-2 bg-white rounded-lg border border-[#E6DED1] px-2.5 py-2">
                        <span className="w-6 h-6 rounded-full bg-[#0B2E4A] text-[#D9A854] text-[8px] font-bold flex items-center justify-center shrink-0">AC</span>
                        <span className="min-w-0">
                          <span className="block text-[8px] font-bold text-[#0B2E4A] leading-tight">Ana Clara</span>
                          <span className="block text-[6.5px] text-[#56636D] leading-tight">Sua travel designer</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="h-3.5 -mx-3 rounded-b-2xl bg-[#1C2B36] border border-white/10 shadow-[0_24px_40px_-16px_rgba(0,0,0,0.6)]" />

          {/* Phone */}
          <div className="absolute -bottom-2 right-0 sm:-right-3 w-32 sm:w-40 rounded-[1.8rem] bg-[#101E29] p-1.5 border border-white/10 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.65)] z-10">
            <div className="rounded-[1.4rem] overflow-hidden bg-[#FAF6EE]">
              <div className="flex items-center justify-between px-3 py-1.5 bg-white border-b border-[#E6DED1]">
                <span className="text-[6.5px] font-bold text-[#0B2E4A]">9:41</span>
                <span className="w-10 h-2 rounded-full bg-[#0B2E4A]" />
                <span className="flex gap-0.5">
                  <span className="w-1 h-1 rounded-full bg-[#56636D]" />
                  <span className="w-1 h-1 rounded-full bg-[#56636D]" />
                </span>
              </div>
              <div className="flex items-center justify-between px-2.5 py-1.5">
                <span className="flex items-center gap-1">
                  <img src="/brand/logo-emblem.png" alt="WYD" className="w-3.5 h-3.5" />
                  <span className="font-display font-semibold text-[#0B2E4A] text-[8px] leading-none">
                    WYD
                    <span className="block text-[3.5px] tracking-[0.18em] text-[#D9A854] uppercase font-sans">Personal travelling</span>
                  </span>
                </span>
                <span className="flex items-center gap-1.5 text-[#56636D]">
                  <Bell size={8} />
                  <MoreHorizontal size={8} />
                </span>
              </div>
              <div className="px-2.5 pb-2">
                <div className="flex items-baseline justify-between">
                  <p className="font-display font-semibold text-[#0B2E4A] text-[10px]">Minha viagem</p>
                  <span className="text-[6.5px] font-bold text-[#7A571C]">Ver tudo</span>
                </div>
                <div className="relative rounded-lg overflow-hidden mt-1">
                  <img src={TRIP_IMG} alt="Costa Amalfitana" className="w-full aspect-[16/9] object-cover" {...LAZY} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B2E4A]/75 to-transparent" />
                  <div className="absolute bottom-1.5 left-1.5">
                    <p className="font-display font-semibold text-white text-[9px] leading-tight">Costa Amalfitana</p>
                    <p className="text-[6px] text-[#F1E7D6]/85">12 – 20 de Maio, 2026</p>
                  </div>
                  <span className="absolute bottom-1.5 right-1.5 w-4 h-4 rounded-full bg-[#D9A854] flex items-center justify-center">
                    <ArrowRight size={8} className="text-[#0B2E4A]" />
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-1 mt-1.5">
                  {QUICK.map((q) => (
                    <div key={q.label} className="text-center">
                      <span className="mx-auto w-6 h-6 rounded-full bg-white border border-[#E6DED1] flex items-center justify-center">
                        <q.icon size={9} className="text-[#0B2E4A]" />
                      </span>
                      <p className="text-[5.5px] font-semibold text-[#56636D] leading-tight mt-0.5">{q.label.split(" ")[0]}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-baseline justify-between mt-2">
                  <p className="font-display font-semibold text-[#0B2E4A] text-[8px]">Próximos passos</p>
                  <span className="text-[5.5px] font-bold text-[#7A571C]">Ver todos</span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 bg-white rounded-md border border-[#E6DED1] px-1.5 py-1.5">
                  <FileText size={8} className="text-[#7A571C] shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[6.5px] font-bold text-[#0B2E4A] leading-tight">Enviar dados dos passaportes</span>
                    <span className="block text-[5px] text-[#56636D] leading-tight">Precisamos dos dados para emitir alguns serviços.</span>
                  </span>
                  <ChevronRight size={8} className="text-[#56636D] shrink-0" />
                </div>
                <p className="mt-2 font-display font-semibold text-[#0B2E4A] text-[8px]">Sua consultora WYD</p>
                <div className="mt-1 flex items-center gap-1.5 bg-white rounded-md border border-[#E6DED1] px-1.5 py-1.5">
                  <span className="w-[18px] h-[18px] rounded-full bg-[#0B2E4A] text-[#D9A854] text-[6px] font-bold flex items-center justify-center shrink-0">AC</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[7px] font-bold text-[#0B2E4A] leading-tight">Ana Clara</span>
                    <span className="block text-[5.5px] text-[#56636D] leading-tight">Travel designer</span>
                  </span>
                  <MessageCircle size={8} className="text-[#D9A854]" />
                  <Phone size={8} className="text-[#0B2E4A]" />
                  <Mail size={8} className="text-[#0B2E4A]" />
                </div>
              </div>
              <div className="flex items-center justify-around px-2 py-1.5 bg-white border-t border-[#E6DED1]">
                {[
                  { icon: Home, label: "Início", active: true },
                  { icon: MapIcon, label: "Roteiro" },
                  { icon: CalendarCheck, label: "Reservas" },
                  { icon: FileText, label: "Docs" },
                  { icon: MoreHorizontal, label: "Mais" },
                ].map((n) => (
                  <span key={n.label} className="flex flex-col items-center gap-0.5">
                    <n.icon size={9} className={n.active ? "text-[#D9A854]" : "text-[#56636D]"} />
                    <span className={`text-[4.5px] font-semibold ${n.active ? "text-[#D9A854]" : "text-[#56636D]"}`}>{n.label}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
